import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  useToast,
  VStack,
  Image,
  Text,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import { CREATE_BEPRO_PLAYER } from '../utils/mutations';
import BeProLogo from '../assets/bepro-logo.png';
import ALLogo from '../assets/AL-Blue-Logo.png';
import { isValidPhoneNumber } from '../utils/phoneUtil';
import { ability, provinces } from '../utils/Select';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  province: '',
  ability: '',
  location: 'Ramage, New Cairo', //'Wakanda, 6 October',
};

const validateEmail = (email: string) => {
  // Simple email regex
  return /\S+@\S+\.\S+/.test(email);
};

const validatePhone = (phone: string) => isValidPhoneNumber(phone);

const paymentUrl =
  'https://accept.paymob.com/standalone/?ref=p_LRR2MlFVNm5JL2lxSmQydUZzdFRsYVU0dz09XzYrVWRYQThCUmFzOFlQa0lmY1NKN0E9PQ';

const BeProRegistration = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const toast = useToast();
  const [createBeProPlayer] = useMutation(CREATE_BEPRO_PLAYER);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingSubmission, setPendingSubmission] = useState<any>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
  };

  const validate = () => {
    const newErrors: any = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(form.email))
      newErrors.email = 'Invalid email address';
    if (!form.phoneNumber.trim())
      newErrors.phoneNumber = 'Phone number is required';
    else if (!validatePhone(form.phoneNumber))
      newErrors.phoneNumber = 'Invalid phone number';
    if (!form.province.trim()) newErrors.province = 'Area is required';
    if (!form.ability.trim()) newErrors.skill = 'Skill level is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setPendingSubmission({ ...form });
    onOpen();
  };

  // Add a helper for registration with paid flag
  const registerPlayer = async (paidValue: boolean) => {
    setIsSubmitting(true);
    setSuccess(false);
    try {
      const { data } = await createBeProPlayer({
        variables: { data: { ...pendingSubmission, paid: paidValue } },
      });
      if (data?.createBeProPlayer?.id) {
        setSuccess(true);
        toast({
          title: paidValue
            ? 'Registration successful!'
            : 'Registration pending payment',
          description: paidValue
            ? 'Your registration has been received.'
            : 'You are registered but you will not participate until you pay for the tryouts.',
          status: paidValue ? 'success' : 'warning',
          isClosable: true,
        });
        setForm(initialState);
        setPendingSubmission(null);
        onClose();
      } else {
        throw new Error('Registration failed.');
      }
    } catch (error: any) {
      let errorMessage = error?.message || 'An error occurred.';
      if (errorMessage.includes('Unique constraint failed')) {
        if (errorMessage.includes('BeProPlayer_email_key')) {
          errorMessage = 'This email address is already registered.';
        } else if (errorMessage.includes('BeProPlayer_phoneNumber_key')) {
          errorMessage = 'This phone number is already registered.';
        } else {
          errorMessage = 'A user with this information already exists.';
        }
      }
      toast({
        title: 'Registration failed',
        description: errorMessage,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentAndSave = async () => {
    await registerPlayer(true);
  };

  const handleCancelAndRegister = async () => {
    await registerPlayer(false);
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Box
        bg="white"
        p={8}
        rounded="lg"
        shadow="lg"
        minW={{ base: '95vw', md: '500px' }}
      >
        <Flex justify="center" mb={0} align="center" gap={4}>
          <Image src={ALLogo} alt="Amateurs League Logo" h="165px" />
          <Image src={BeProLogo} alt="BePro Logo" h="70px" />
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={4}>
          Tryouts Registration
          <br />
          <span style={{ fontWeight: 400, fontSize: '1rem' }}>
            Location: Ramage, New Cairo
            <br />
            Date and Time: Tuesday 26/8 - 9.30 PM
          </span>
        </Text>
        <Text textAlign="center" color="red.500" fontWeight="bold" mb={4}>
          Tryouts payment is for EGP 100.
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.firstName} isRequired>
              <FormLabel>First Name</FormLabel>
              <Input
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                autoComplete="given-name"
              />
              {errors.firstName && (
                <Text color="red.500" fontSize="sm">
                  {errors.firstName}
                </Text>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.lastName} isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                autoComplete="family-name"
              />
              {errors.lastName && (
                <Text color="red.500" fontSize="sm">
                  {errors.lastName}
                </Text>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.email} isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                autoComplete="email"
              />
              {errors.email && (
                <Text color="red.500" fontSize="sm">
                  {errors.email}
                </Text>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.phoneNumber} isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="Enter your phone number"
                autoComplete="tel"
              />
              {errors.phoneNumber && (
                <Text color="red.500" fontSize="sm">
                  {errors.phoneNumber}
                </Text>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.province} isRequired>
              <FormLabel>What area are you from?</FormLabel>
              <Select
                name="province"
                value={form.province}
                onChange={handleChange}
                placeholder="Select your area"
                required
              >
                {provinces.map((prov) => (
                  <option value={prov} key={prov}>
                    {prov}
                  </option>
                ))}
              </Select>
              {errors.province && (
                <Text color="red.500" fontSize="sm">
                  {errors.province}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Tryouts Location</FormLabel>
              <Input
                name="location"
                value={form.location}
                isReadOnly
                bg="gray.100"
                cursor="not-allowed"
              />
            </FormControl>
            <FormControl isInvalid={!!errors.skill} isRequired>
              <FormLabel>
                How would you describe your football skill level?
              </FormLabel>
              <Select
                name="ability"
                value={form.ability}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleChange(e)
                }
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  borderColor: errors.skill ? 'red' : '#E2E8F0',
                }}
                required
              >
                <option value="">Select your skill level</option>
                {ability.map((s) => (
                  <option value={s} key={s}>
                    {s}
                  </option>
                ))}
              </Select>
              {errors.skill && (
                <Text color="red.500" fontSize="sm">
                  {errors.skill}
                </Text>
              )}
            </FormControl>
            <Button
              type="submit"
              colorScheme="teal"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              size="lg"
              mt={2}
            >
              Register
            </Button>
            {success && (
              <Text color="green.500" textAlign="center" fontWeight="bold">
                Registration successful! Thank you for registering.
              </Text>
            )}
          </VStack>
        </form>
      </Box>
      <Modal isOpen={isOpen} onClose={handleCancelAndRegister} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Tryouts Payment Required</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              Tryouts payment is <b>EGP 100</b>.
            </Text>
            <Text mb={4}>
              Please complete your payment before finalizing your registration.
            </Text>
            <Link
              href={paymentUrl}
              color="teal.600"
              isExternal
              fontWeight="bold"
              onClick={handlePaymentAndSave}
            >
              Click here to pay and complete registration
            </Link>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleCancelAndRegister} variant="ghost">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default BeProRegistration;
